import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface SubsectionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
  delay?: number;
}

const SubsectionCard: React.FC<SubsectionCardProps> = ({
  title,
  description,
  icon,
  color,
  onClick,
  delay = 0,
}) => {
  return (
    <motion.div
      className="relative bg-white rounded-2xl p-6 cursor-pointer overflow-hidden group border border-gray-200"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.4, ease: 'easeOut' }}
      whileHover={{ 
        y: -8, 
        scale: 1.02,
        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
      }}
      onClick={onClick}
    >
      {/* Animated background gradient */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)`,
        }}
      />

      {/* Animated orb */}
      <motion.div
        className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-0 group-hover:opacity-20 blur-2xl"
        style={{ background: color }}
        animate={{
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex items-start gap-4">
        {/* Icon container */}
        <motion.div
          className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
          }}
          whileHover={{ rotate: 5, scale: 1.1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <div style={{ color }}>{icon}</div>
        </motion.div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-gray-700 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2">
            {description}
          </p>
        </div>

        {/* Arrow icon */}
        <motion.div
          className="flex-shrink-0"
          initial={{ x: 0 }}
          whileHover={{ x: 4 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <ChevronRightIcon 
            className="w-6 h-6 text-gray-400 group-hover:text-gray-600 transition-colors" 
            style={{ color: `${color}80` }}
          />
        </motion.div>
      </div>

      {/* Bottom accent line */}
      <motion.div
        className="absolute bottom-0 left-0 h-1 rounded-full"
        style={{ background: color }}
        initial={{ width: 0 }}
        whileHover={{ width: '100%' }}
        transition={{ duration: 0.3 }}
      />

      {/* Shine effect */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${color}15 50%, transparent 100%)`,
        }}
        initial={{ x: '-100%' }}
        whileHover={{ x: '100%' }}
        transition={{ duration: 0.6 }}
      />
    </motion.div>
  );
};

export default SubsectionCard;

